import { useEffect, useRef } from "react";

/**
 * Canvas that samples the "TRYB" wordmark into pixel targets, spawns particles
 * at random off-canvas positions, and animates them to their targets over ~1.4s.
 * After assembly, particles gently breathe.
 *
 * Rendered as a decorative background layer inside the hero.
 * - Respects prefers-reduced-motion (renders static text instead).
 * - Auto-resizes with the window.
 */
export const ParticleWordmark = ({ text = "TRYB", className = "", opacity = 0.6 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;
    let particles = [];
    let assembledAt = 0;
    let start = performance.now();

    const sampleTargets = () => {
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Draw the wordmark off-screen (invisible) to sample pixel positions
      const sample = document.createElement("canvas");
      sample.width = W;
      sample.height = H;
      const sctx = sample.getContext("2d");
      if (!sctx) return [];

      // Big, bold, TRYB — tuned to feel roughly hero-sized
      const fontSize = Math.min(W * 0.28, H * 0.68, 380);
      sctx.fillStyle = "#fff";
      sctx.font = `900 ${fontSize}px "Space Grotesk", system-ui, sans-serif`;
      sctx.textAlign = "center";
      sctx.textBaseline = "middle";
      sctx.fillText(text, W / 2, H / 2);

      const { data } = sctx.getImageData(0, 0, W, H);
      const targets = [];
      // Higher step = fewer particles = better perf. 4-5 is a good sweet spot.
      const step = W < 640 ? 6 : 4;
      for (let y = 0; y < H; y += step) {
        for (let x = 0; x < W; x += step) {
          const idx = (y * W + x) * 4;
          if (data[idx + 3] > 128) targets.push({ x, y });
        }
      }
      return targets;
    };

    const rebuild = () => {
      const targets = sampleTargets();
      if (!targets.length) return;
      particles = targets.map((t) => ({
        tx: t.x,
        ty: t.y,
        // Spawn from random position OUTSIDE the canvas rect
        x: Math.random() * W * 2 - W / 2,
        y: Math.random() * H * 2 - H / 2,
        // Slight per-particle phase for breathing after assembly
        phase: Math.random() * Math.PI * 2,
        // Slight per-particle colour shift along the brand gradient
        hue: 220 + Math.random() * 100,
      }));
      start = performance.now();
      assembledAt = 0;
    };

    const draw = (now) => {
      const elapsed = now - start;
      const assemble = Math.min(elapsed / 1400, 1); // 1.4s to assemble
      // Ease-out cubic
      const ease = 1 - Math.pow(1 - assemble, 3);
      if (assemble >= 1 && !assembledAt) assembledAt = now;

      ctx.clearRect(0, 0, W, H);

      for (const p of particles) {
        // Position: linear-interp from spawn → target, eased
        let x = p.x + (p.tx - p.x) * ease;
        let y = p.y + (p.ty - p.y) * ease;

        // Breathing after assembly (subtle, only 1-2px shimmer)
        if (assembledAt) {
          const t = (now - assembledAt) / 1000;
          x += Math.cos(t + p.phase) * 0.9;
          y += Math.sin(t * 0.9 + p.phase) * 0.9;
        }

        const size = assemble < 1 ? 2.2 - assemble * 0.9 : 1.35;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 100%, 65%, ${0.35 + ease * 0.4})`;
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const drawStatic = () => {
      const rect = canvas.getBoundingClientRect();
      W = rect.width; H = rect.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      const fontSize = Math.min(W * 0.28, H * 0.68, 380);
      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, "hsla(221,100%,55%,0.35)");
      grad.addColorStop(0.5, "hsla(270,100%,60%,0.35)");
      grad.addColorStop(1, "hsla(320,100%,55%,0.35)");
      ctx.fillStyle = grad;
      ctx.font = `900 ${fontSize}px "Space Grotesk", system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, W / 2, H / 2);
    };

    if (reduce) {
      drawStatic();
      const onResize = () => drawStatic();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    rebuild();
    rafRef.current = requestAnimationFrame(draw);

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuild, 200);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [text]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      style={{ opacity }}
      data-testid="particle-wordmark"
    />
  );
};

export default ParticleWordmark;
